import { isRegisteredPanelId } from './panelRegistry';

export const PANEL_URL_KEY = 'panel' as const;

/**
 * Parses the `?panel=<id>` query parameter. Returns the panel ID if it
 * is a registered deep-linkable panel, null otherwise.
 *
 * Accepts a raw search string ("?panel=amenities"), URLSearchParams, or
 * a Next.js router.query-style Record.
 */
export function parsePanelFromUrl(
  source: string | URLSearchParams | Record<string, string | Array<string> | undefined>,
): string | null {
  let raw: string | undefined;

  if (typeof source === 'string') {
    const params = new URLSearchParams(source);
    raw = params.get(PANEL_URL_KEY) ?? undefined;
  } else if (source instanceof URLSearchParams) {
    raw = source.get(PANEL_URL_KEY) ?? undefined;
  } else {
    const val = source[PANEL_URL_KEY];
    raw = Array.isArray(val) ? val[0] : val;
  }

  if (!raw) return null;

  return isRegisteredPanelId(raw) ? raw : null;
}

/**
 * Serializes a panel ID into the current URL's search params.
 * Returns the full URL string (pathname + search + hash).
 * If panelId is null, removes the panel param.
 *
 * Pure URL manipulation — the caller decides whether to use
 * pushState or replaceState.
 */
export function serializePanelToUrl(panelId: string | null): string {
  const params = new URLSearchParams(window.location.search);

  if (panelId) {
    params.set(PANEL_URL_KEY, panelId);
  } else {
    params.delete(PANEL_URL_KEY);
  }

  const search = params.toString();
  const hash = window.location.hash;
  return search ? `${window.location.pathname}?${search}${hash}` : `${window.location.pathname}${hash}`;
}
